
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Dealer } from '@/types/dealer';
import { DealerFilterState } from '../types';
import { fetchDealersDataPaginated } from '../dealersServicePaginated';
import { InfiniteScrollState } from './types';

interface UseFetchDealersProps {
  pageSize: number;
  filters: DealerFilterState;
  coordinates?: { latitude: number; longitude: number } | null;
  city?: string;
  state: InfiniteScrollState;
  actions: {
    setDealers: React.Dispatch<React.SetStateAction<Dealer[]>>;
    setTotalCount: React.Dispatch<React.SetStateAction<number>>;
    setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
  };
}

export const useFetchDealers = ({
  pageSize,
  filters,
  coordinates,
  city,
  state,
  actions
}: UseFetchDealersProps) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch dealers with pagination
  const fetchDealers = useCallback(async (
    page: number, 
    append: boolean = false,
    signal?: AbortSignal
  ) => {
    console.log(`🔄 useFetchDealers: Starting fetch for page ${page}, append: ${append}`);
    
    try {
      const result = await fetchDealersDataPaginated({
        page,
        limit: pageSize,
        filters,
        coordinates: coordinates ? {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude
        } : undefined,
        city,
        signal
      });

      if (signal?.aborted) {
        console.log("🚫 useFetchDealers: Request was aborted");
        return;
      }

      const { data: newDealers, totalCount: newTotalCount, hasMore: moreAvailable } = result;

      console.log(`📊 useFetchDealers: Fetch result for page ${page}:`, {
        newDealersCount: newDealers.length,
        totalCount: newTotalCount,
        hasMore: moreAvailable,
        append
      });

      if (append) {
        console.log("📈 useFetchDealers: Appending dealers to existing list");
        actions.setDealers(prev => {
          // Evita duplicati
          const existingIds = new Set(prev.map(d => d.id));
          const uniqueNewDealers = newDealers.filter(d => !existingIds.has(d.id));
          console.log(`🔄 useFetchDealers: Adding ${uniqueNewDealers.length} unique dealers to ${prev.length} existing`);
          return [...prev, ...uniqueNewDealers];
        });
      } else {
        console.log(`🔄 useFetchDealers: Setting ${newDealers.length} dealers (replacing existing)`);
        actions.setDealers(newDealers);
      }

      actions.setTotalCount(newTotalCount);
      actions.setHasMore(moreAvailable);
      actions.setError(null);

      console.log(`✅ useFetchDealers: Successfully processed page ${page}`);

    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log("🚫 useFetchDealers: Request aborted");
        return;
      }
      
      console.error('💥 useFetchDealers: Error fetching dealers:', err);
      actions.setError(err.message || 'Errore nel caricamento dei compro oro');
      
      if (!append) {
        console.log("🔄 useFetchDealers: Clearing dealers due to error");
        actions.setDealers([]);
        actions.setTotalCount(0);
        actions.setHasMore(false);
      }
    }
  }, [pageSize, filters, coordinates, city, actions]);

  return {
    fetchDealers,
    abortControllerRef
  };
};
